import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { parentApi, cantineApi, paymentsOnlineApi, gradesApi, schoolConfigApi, studentAttendanceApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { GraduationCap, Money, EnvelopeSimple, FilePdf, PaperPlaneTilt, Eye, ForkKnife, Bell, CreditCard, Key, UserPlus, Scales, Warning, ShieldCheck, PaperclipHorizontal, XCircle } from '@phosphor-icons/react';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import PushToggleCard from '../components/dashboard/PushToggleCard';
import ParentBroadcasts from '../components/parent/ParentBroadcasts';
import { toast } from 'sonner';

const ParentDashboard = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [availPeriods, setAvailPeriods] = useState([]); // v2 bulletins available
  const [downloadingBulletin, setDownloadingBulletin] = useState(null);
  const [payments, setPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]); // aggregated across ALL linked children (used by the "Comparaison" tab)
  const [absences, setAbsences] = useState([]);
  const [absencesLoading, setAbsencesLoading] = useState(false);
  const [justifyOpen, setJustifyOpen] = useState(false);
  const [justifyTarget, setJustifyTarget] = useState(null);
  const [justifyReason, setJustifyReason] = useState('');
  const [justifyFileB64, setJustifyFileB64] = useState('');
  const [justifyFileName, setJustifyFileName] = useState('');
  const [justifySubmitting, setJustifySubmitting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [todayMenu, setTodayMenu] = useState(null);
  const [todayMenuLoading, setTodayMenuLoading] = useState(true);
  const [payingCategory, setPayingCategory] = useState(null); // currently launching checkout
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  // "+ Ajouter un enfant" — small link-child modal
  const [linkChildOpen, setLinkChildOpen] = useState(false);
  const [linkChildCode, setLinkChildCode] = useState('');
  const [linkChildSubmitting, setLinkChildSubmitting] = useState(false);
  // GeniusPay amount coefficient (1.0 = pas de frais, 1.05 = +5% frais).
  // Chargé une seule fois au montage depuis l'endpoint public. Utilisé pour
  // afficher le breakdown de transparence sur les postes payables.
  const [feeCoef, setFeeCoef] = useState(1.0);

  // Polling effect: when redirected back from Stripe with ?session_id=..., poll the status until paid
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    let attempts = 0;
    const maxAttempts = 8;
    const interval = 2000;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await paymentsOnlineApi.status(sessionId);
        if (data.payment_status === 'paid') {
          toast.success(
            language === 'fr'
              ? `Paiement confirmé : ${Number(data.amount_xof || 0).toLocaleString('fr-FR')} FCFA — ${data.label}`
              : `Payment confirmed: ${Number(data.amount_xof || 0).toLocaleString('en-US')} FCFA — ${data.label}`,
            { duration: 8000 }
          );
          // Clean session_id from URL & reload data
          window.history.replaceState({}, document.title, window.location.pathname);
          loadData();
          return;
        }
        if (data.status === 'expired') {
          toast.error(language === 'fr' ? 'Session de paiement expirée' : 'Payment session expired');
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        }
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(tick, interval);
        } else {
          if (data.stripe_error) {
            toast.info(language === 'fr' ? 'Paiement en cours de confirmation. Si la carte a été débitée, le solde sera mis à jour sous quelques minutes.' : 'Payment confirmation pending. If your card was charged, the balance will update shortly.');
          } else {
            toast.info(language === 'fr' ? 'Paiement en cours… consultez l\'historique dans quelques minutes.' : 'Payment processing... check history shortly.');
          }
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (e) {
        // Silent fail — likely transient. Stop polling.
        console.warn('Polling stopped:', e.message);
      }
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const payOnline = async (category) => {
    if (!selectedChild || !category) return;
    setPayingCategory(category);
    try {
      const origin = window.location.origin;
      const { data } = await paymentsOnlineApi.checkout(selectedChild.id, category, origin);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'fr' ? 'Impossible de lancer le paiement' : 'Failed to start payment'));
      setPayingCategory(null);
    }
  };

  const payOnlineGeniusPay = async (category) => {
    if (!selectedChild || !category) return;
    setPayingCategory(category);
    try {
      const origin = window.location.origin;
      const { data } = await paymentsOnlineApi.geniuspayCheckout(selectedChild.id, category, origin);
      // Remember the reference so /payment/success can verify it.
      try { sessionStorage.setItem('geniuspay_last_ref', data.reference); } catch (_e) { /* noop */ }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'fr' ? 'Impossible de lancer le paiement Mobile Money' : 'Failed to start Mobile Money payment'));
      setPayingCategory(null);
    }
  };

  /**
   * One-click Mobile Money checkout for the "priority" child directly from
   * the Comparaison tab. Picks the category with the largest outstanding
   * balance from the given payment info and launches GeniusPay for that
   * specific student (does not depend on `selectedChild`).
   */
  const payPriorityGeniusPay = async (paymentInfo) => {
    if (!paymentInfo) return;
    const categories = [
      { key: 'registration', section: paymentInfo.registration,
        // registration vs reregistration is decided by the label returned by the backend
        payKey: paymentInfo.registration?.label?.toLowerCase().includes('rein') ? 'reregistration' : 'registration' },
      { key: 'tuition', section: paymentInfo.tuition, payKey: 'tuition' },
      { key: 'canteen', section: paymentInfo.canteen, payKey: 'canteen' },
      { key: 'transport', section: paymentInfo.transport, payKey: 'transport' },
    ].filter(c => (c.section?.balance || 0) > 0);
    if (categories.length === 0) {
      toast.info(language === 'fr' ? 'Aucun solde à régler.' : 'Nothing to pay.');
      return;
    }
    categories.sort((a, b) => (b.section.balance || 0) - (a.section.balance || 0));
    const top = categories[0];
    const flightKey = `priority:${paymentInfo.student_id}`;
    setPayingCategory(flightKey);
    try {
      const origin = window.location.origin;
      const { data } = await paymentsOnlineApi.geniuspayCheckout(
        paymentInfo.student_id, top.payKey, origin,
      );
      try { sessionStorage.setItem('geniuspay_last_ref', data.reference); } catch (_e) { /* noop */ }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'fr' ? 'Impossible de lancer le paiement Mobile Money' : 'Failed to start Mobile Money payment'));
      setPayingCategory(null);
    }
  };

  const [loading, setLoading] = useState(true);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageDetailOpen, setMessageDetailOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  
  const [newMessage, setNewMessage] = useState({
    subject: '',
    content: '',
    student_id: ''
  });

  useEffect(() => {
    loadData();
    // Load school-wide GeniusPay fee coefficient (public endpoint).
    schoolConfigApi.getPaymentSettings()
      .then(({ data }) => {
        const v = Number(data?.geniuspay_amount_coefficient ?? 1.0);
        if (v >= 1 && v <= 2) setFeeCoef(v);
      })
      .catch(() => { /* silent — default remains 1.0 (no breakdown shown) */ });
  }, []);

  useEffect(() => {
    if (selectedChild) {
      loadChildData(selectedChild.id);
      loadAbsences(selectedChild.id);
    }
  }, [selectedChild]);

  const loadData = async () => {
    try {
      const [childrenRes, messagesRes, allPaymentsRes] = await Promise.all([
        parentApi.getChildren(),
        parentApi.getMessages(),
        parentApi.getPayments(), // no student_id → returns array for ALL linked children
      ]);
      setChildren(childrenRes.data);
      setMessages(messagesRes.data);
      setAllPayments(allPaymentsRes.data || []);

      if (childrenRes.data.length > 0) {
        setSelectedChild(childrenRes.data[0]);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
    // Today's menu (independent of children)
    try {
      const { data } = await cantineApi.todayMenu();
      setTodayMenu(data);
    } catch (e) {
      // Backend returns 200 + null when no menu published; only catches real errors
      console.error('Failed to load today menu:', e);
    } finally {
      setTodayMenuLoading(false);
    }
  };

  const loadChildData = async (studentId) => {
    try {
      const [paymentsRes, periodsRes] = await Promise.all([
        parentApi.getPayments(studentId),
        gradesApi.availablePeriods(studentId),
      ]);
      setPayments(paymentsRes.data);
      setAvailPeriods(periodsRes.data || []);
    } catch (error) {
      console.error('Failed to load child data:', error);
    }
  };

  const handleDownloadV2Bulletin = async (entry) => {
    const key = `${entry.class_name}::${entry.period_key}`;
    setDownloadingBulletin(key);
    try {
      const res = await gradesApi.bulletinPdf(
        selectedChild.id, entry.class_name, entry.period_key, entry.school_year,
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulletin_${selectedChild.last_name || ''}_${selectedChild.first_name || ''}_${entry.period_key}.pdf`.replace(/\s+/g, '_');
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Téléchargement échoué');
    } finally { setDownloadingBulletin(null); }
  };

  const loadAbsences = async (targetChildId) => {
    setAbsencesLoading(true);
    try {
      const { data } = await parentApi.absences({
        studentId: targetChildId,
        daysBack: 60,
      });
      setAbsences(data.items || []);
    } catch (e) {
      // silent — tab will show empty state
    } finally {
      setAbsencesLoading(false);
    }
  };

  const openJustify = (absence) => {
    setJustifyTarget(absence);
    setJustifyReason('');
    setJustifyFileB64('');
    setJustifyFileName('');
    setJustifyOpen(true);
  };

  const onPickJustifyFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) {
      toast.error(language === 'fr' ? 'Fichier trop volumineux (>3 Mo)' : 'File too large (>3 MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setJustifyFileB64(String(reader.result || ''));
      setJustifyFileName(f.name);
    };
    reader.readAsDataURL(f);
  };

  const submitJustify = async () => {
    if (!justifyTarget || !justifyReason.trim()) return;
    setJustifySubmitting(true);
    try {
      await studentAttendanceApi.justify(
        justifyTarget._mongo_id || justifyTarget.id,
        {
          reason: justifyReason.trim(),
          document_base64: justifyFileB64 || undefined,
          document_filename: justifyFileName || undefined,
        }
      );
      toast.success(language === 'fr' ? 'Absence justifiée' : 'Absence justified');
      setJustifyOpen(false);
      if (selectedChild) loadAbsences(selectedChild.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur de justification' : 'Justification failed'));
    } finally {
      setJustifySubmitting(false);
    }
  };


  const handleLinkChild = async (e) => {
    e.preventDefault();
    const code = linkChildCode.trim();
    if (!code) return;
    setLinkChildSubmitting(true);
    try {
      const { data } = await parentApi.linkChild(code);
      if (data.already_linked) {
        toast.info(
          language === 'fr'
            ? `${data.student_name} est déjà lié à votre compte.`
            : `${data.student_name} is already linked to your account.`
        );
      } else {
        toast.success(
          language === 'fr'
            ? `${data.student_name} a été ajouté avec succès.`
            : `${data.student_name} was linked successfully.`
        );
      }
      setLinkChildOpen(false);
      setLinkChildCode('');
      // Reload children + aggregate payments (used by the "Comparaison" tab)
      const [childrenRes, allPaymentsRes] = await Promise.all([
        parentApi.getChildren(),
        parentApi.getPayments(),
      ]);
      setChildren(childrenRes.data);
      setAllPayments(allPaymentsRes.data || []);
      const added = childrenRes.data.find((c) => c.id === data.student_id);
      if (added) setSelectedChild(added);
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          (language === 'fr' ? 'Code élève invalide' : 'Invalid student code')
      );
    } finally {
      setLinkChildSubmitting(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    try {
      await parentApi.sendMessage({
        ...newMessage,
        recipient_id: 'school',
        student_id: selectedChild?.id || newMessage.student_id
      });
      toast.success(language === 'fr' ? 'Message envoyé' : 'Message sent');
      setMessageDialogOpen(false);
      setNewMessage({ subject: '', content: '', student_id: '' });
      loadData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleViewMessage = async (message) => {
    try {
      const { data } = await parentApi.getMessageDetail(message.id);
      setSelectedMessage(data);
      setMessageDetailOpen(true);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) return;
    try {
      await parentApi.replyMessage(selectedMessage.id, { content: replyContent });
      toast.success(language === 'fr' ? 'Réponse envoyée' : 'Reply sent');
      setReplyContent('');
      const { data } = await parentApi.getMessageDetail(selectedMessage.id);
      setSelectedMessage(data);
      loadData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  const currentPaymentInfo = payments.find(p => p.student_id === selectedChild?.id);

  // Find the fee category with the largest remaining balance — used by the "next due" card
  const nextDue = (() => {
    if (!currentPaymentInfo) return null;
    const categories = [
      { key: 'registration', label: currentPaymentInfo.registration?.label || (language === 'fr' ? 'Inscription' : 'Registration'), section: currentPaymentInfo.registration },
      { key: 'tuition', label: language === 'fr' ? 'Scolarité' : 'Tuition', section: currentPaymentInfo.tuition },
      { key: 'canteen', label: language === 'fr' ? 'Cantine' : 'Canteen', section: currentPaymentInfo.canteen },
      { key: 'transport', label: language === 'fr' ? 'Transport' : 'Transport', section: currentPaymentInfo.transport },
    ];
    const withBalance = categories.filter(c => (c.section?.balance || 0) > 0);
    if (withBalance.length === 0) return null;
    withBalance.sort((a, b) => (b.section.balance || 0) - (a.section.balance || 0));
    return withBalance[0];
  })();

  const openContactSchoolDialog = () => {
    if (!nextDue) return;
    setNewMessage({
      subject: language === 'fr'
        ? `Demande concernant le paiement: ${nextDue.label}`
        : `Question about payment: ${nextDue.label}`,
      content: language === 'fr'
        ? `Bonjour,\n\nJe souhaite m'organiser pour régler le solde restant de ${formatCurrency(nextDue.section.balance)} sur le poste "${nextDue.label}" pour ${selectedChild?.first_name} ${selectedChild?.last_name}.\n\nPouvez-vous me confirmer les modalités ?\n\nCordialement.`
        : `Hello,\n\nI would like to arrange payment of the remaining ${formatCurrency(nextDue.section.balance)} for "${nextDue.label}" for ${selectedChild?.first_name} ${selectedChild?.last_name}.\n\nCould you please confirm the next steps?\n\nBest regards.`,
      student_id: selectedChild?.id || ''
    });
    setMessageDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#10B981]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-dashboard">
      {/* Welcome Banner */}
      <div className="relative rounded-lg overflow-hidden bg-gradient-to-r from-[#10B981] to-[#059669] h-40">
        <div className="absolute inset-0 bg-[url('https://static.prod-images.emergentagent.com/jobs/568a569c-0fa6-40dc-9d3f-d310e1c010bc/images/6c3e65fdc098a236208decff79887da29de5eaab226836321278bd67cc31fe5d.png')] bg-cover bg-center opacity-20" />
        <div className="relative z-10 h-full flex flex-col justify-center px-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-outfit text-3xl font-bold text-white mb-2">
                {t('welcome')}, {user?.name}!
              </h1>
              <p className="font-manrope text-white/90">
                {t('parentPortal')}
              </p>
            </div>
            <Button
              onClick={() => setPwDialogOpen(true)}
              variant="ghost"
              className="text-white hover:bg-white/20"
              data-testid="parent-change-password-btn"
            >
              <Key size={18} className="mr-2" />
              {language === 'fr' ? 'Mot de passe' : 'Password'}
            </Button>
          </div>
        </div>
      </div>

      <ChangePasswordDialog open={pwDialogOpen} onOpenChange={setPwDialogOpen} />

      {/* Push notifications toggle (parent) */}
      <PushToggleCard />

      {/* iter103 — School group messages + reply */}
      <ParentBroadcasts isFR={language === 'fr'} />

      {/* Child Selector & "+ Ajouter un enfant" */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              <Label className="font-manrope font-medium">{t('myChildren')}:</Label>
              {children.length > 1 ? (
                <Select value={selectedChild?.id} onValueChange={(id) => setSelectedChild(children.find(c => c.id === id))}>
                  <SelectTrigger className="w-[250px] border-[#E2E8F0]" data-testid="child-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {children.map(child => (
                      <SelectItem key={child.id} value={child.id}>
                        {child.first_name} {child.last_name} - {child.class_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="font-manrope text-sm text-[#475569]" data-testid="child-single-label">
                  {selectedChild
                    ? `${selectedChild.first_name} ${selectedChild.last_name}${selectedChild.class_id ? ` — ${selectedChild.class_id}` : ''}`
                    : (language === 'fr' ? 'Aucun enfant lié' : 'No linked child')}
                </span>
              )}
            </div>
            <Button
              onClick={() => setLinkChildOpen(true)}
              variant="outline"
              className="border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10 shrink-0"
              data-testid="add-child-btn"
            >
              <UserPlus size={18} className="mr-2" />
              {language === 'fr' ? 'Ajouter un enfant' : 'Add a child'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Next-due reminder card (highlights largest outstanding balance) */}
      {selectedChild && nextDue && (
        <Card
          className="border-l-4 border-l-amber-500 border-y border-r border-[#E2E8F0] shadow-none bg-gradient-to-r from-amber-50/60 to-transparent"
          data-testid="parent-next-due-card"
        >
          <CardContent className="p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2.5 rounded-lg bg-amber-100">
                  <Bell size={22} className="text-amber-700" weight="duotone" />
                </div>
                <div className="space-y-1">
                  <p className="font-outfit text-sm font-semibold text-amber-900 uppercase tracking-wide">
                    {language === 'fr' ? 'Prochaine échéance' : 'Next due'}
                  </p>
                  <p className="font-manrope text-[#0F172A]">
                    {language === 'fr' ? 'Prochain paiement suggéré:' : 'Suggested next payment:'}{' '}
                    <span className="font-semibold" data-testid="next-due-label">{nextDue.label}</span>{' '}
                    — <span className="font-outfit text-lg font-bold text-amber-700" data-testid="next-due-amount">
                      {formatCurrency(nextDue.section.balance)}
                    </span>{' '}
                    <span className="text-sm text-[#64748B]">{language === 'fr' ? 'restants' : 'remaining'}</span>
                  </p>
                  {nextDue.section.discount_percent > 0 && (
                    <p className="text-xs text-amber-800/80">
                      {language === 'fr' ? 'Une remise de' : 'Discount of'} -{nextDue.section.discount_percent}% {language === 'fr' ? 'est déjà appliquée sur ce poste.' : 'already applied.'}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 shrink-0 flex-wrap">
                <Button
                  onClick={() => payOnline(nextDue.key === 'registration' ? (currentPaymentInfo.registration?.label?.toLowerCase().includes('rein') ? 'reregistration' : 'registration') : nextDue.key)}
                  disabled={payingCategory !== null}
                  className="bg-[#0052FF] hover:bg-[#0040CC] text-white"
                  data-testid="pay-now-btn"
                >
                  <CreditCard size={16} className="mr-2" />
                  {payingCategory ? (language === 'fr' ? 'Chargement…' : 'Loading…') : (language === 'fr' ? 'Payer par carte (Stripe)' : 'Pay by card (Stripe)')}
                </Button>
                <Button
                  onClick={() => payOnlineGeniusPay(nextDue.key === 'registration' ? (currentPaymentInfo.registration?.label?.toLowerCase().includes('rein') ? 'reregistration' : 'registration') : nextDue.key)}
                  disabled={payingCategory !== null}
                  className="bg-[#10B981] hover:bg-[#059669] text-white"
                  data-testid="pay-now-geniuspay-btn"
                >
                  <CreditCard size={16} className="mr-2" />
                  {payingCategory ? (language === 'fr' ? 'Chargement…' : 'Loading…') : (language === 'fr' ? 'Mobile Money (Wave / Orange / MTN)' : 'Mobile Money (Wave / Orange / MTN)')}
                </Button>
                <Button
                  onClick={openContactSchoolDialog}
                  variant="outline"
                  className="border-amber-300 text-amber-800 hover:bg-amber-100"
                  data-testid="contact-school-btn"
                >
                  <EnvelopeSimple size={16} className="mr-2" />
                  {language === 'fr' ? "Contacter l'école" : 'Contact the school'}
                </Button>
              </div>
              <p
                className="text-xs font-semibold text-red-600 mt-2"
                data-testid="payment-fee-warning"
              >
                {language === 'fr'
                  ? '⚠ Des frais additionnels de transaction seront facturés.'
                  : '⚠ Additional transaction fees will be applied.'}
              </p>
              {/* Fee transparency breakdown — only when the school applies a coefficient > 1 */}
              {feeCoef > 1.001 && (
                <div
                  className="mt-2 p-3 rounded-lg bg-white border border-red-200 text-xs font-manrope"
                  data-testid="payment-fee-breakdown"
                >
                  <p className="font-semibold text-[#0F172A] mb-1.5">
                    {language === 'fr' ? 'Détail du montant envoyé à Mobile Money :' : 'Amount breakdown sent to Mobile Money:'}
                  </p>
                  <div className="space-y-0.5 text-[#475569]">
                    <div className="flex justify-between">
                      <span>{language === 'fr' ? 'Base' : 'Base'}</span>
                      <span className="font-mono" data-testid="fee-breakdown-base">
                        {formatCurrency(nextDue.section.balance)}
                      </span>
                    </div>
                    <div className="flex justify-between text-red-600">
                      <span>
                        {language === 'fr' ? 'Frais de transaction' : 'Transaction fees'} ({((feeCoef - 1) * 100).toFixed(2).replace(/\.?0+$/, '')}%)
                      </span>
                      <span className="font-mono" data-testid="fee-breakdown-fees">
                        +{formatCurrency(nextDue.section.balance * (feeCoef - 1))}
                      </span>
                    </div>
                    <div className="border-t border-slate-200 pt-1 mt-1 flex justify-between font-semibold text-[#0F172A]">
                      <span>{language === 'fr' ? 'Total à payer' : 'Total to pay'}</span>
                      <span className="font-mono text-emerald-700" data-testid="fee-breakdown-total">
                        {formatCurrency(nextDue.section.balance * feeCoef)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards (aggregate across tuition + canteen + transport + registration) */}
      {selectedChild && currentPaymentInfo && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="parent-summary-cards">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-manrope text-sm text-[#64748B]">{t('totalDue')}</p>
                  <p className="font-outfit text-2xl font-bold text-[#0F172A]" data-testid="parent-total-due">
                    {formatCurrency(currentPaymentInfo.total?.expected)}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50">
                  <Money size={24} className="text-blue-600" weight="duotone" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-manrope text-sm text-[#64748B]">{t('amountPaid')}</p>
                  <p className="font-outfit text-2xl font-bold text-[#10B981]" data-testid="parent-total-paid">
                    {formatCurrency(currentPaymentInfo.total?.paid)}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-green-50">
                  <Money size={24} className="text-green-600" weight="duotone" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-manrope text-sm text-[#64748B]">{t('remainingBalance')}</p>
                  <p className="font-outfit text-2xl font-bold text-[#EF4444]" data-testid="parent-total-balance">
                    {formatCurrency(currentPaymentInfo.total?.balance)}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-red-50">
                  <Money size={24} className="text-red-600" weight="duotone" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Fee Breakdown by Category (always visible when payment info available) */}
      {selectedChild && currentPaymentInfo && (
        <Card className="border border-[#E2E8F0] shadow-none" data-testid="parent-fee-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-base">{language === 'fr' ? 'Détail des frais' : 'Fee breakdown'}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-[#E2E8F0]">
                  <TableHead className="font-outfit font-semibold">{language === 'fr' ? 'Poste' : 'Category'}</TableHead>
                  <TableHead className="font-outfit font-semibold text-right">{language === 'fr' ? 'Frais nets' : 'Net fee'}</TableHead>
                  <TableHead className="font-outfit font-semibold text-right">{language === 'fr' ? 'Payé' : 'Paid'}</TableHead>
                  <TableHead className="font-outfit font-semibold text-right">{language === 'fr' ? 'Solde' : 'Balance'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  { key: 'registration', label: currentPaymentInfo.registration?.label || (language === 'fr' ? 'Inscription' : 'Registration'), data: currentPaymentInfo.registration },
                  { key: 'tuition', label: language === 'fr' ? 'Scolarité' : 'Tuition', data: currentPaymentInfo.tuition },
                  { key: 'canteen', label: language === 'fr' ? 'Cantine' : 'Canteen', data: currentPaymentInfo.canteen },
                  { key: 'transport', label: language === 'fr' ? 'Transport' : 'Transport', data: currentPaymentInfo.transport },
                ].filter(row => row.data && (row.data.discounted_amount > 0 || row.data.paid_amount > 0)).map(row => (
                  <TableRow key={row.key} className="border-[#E2E8F0]" data-testid={`fee-row-${row.key}`}>
                    <TableCell className="font-manrope">
                      {row.label}
                      {row.data.discount_percent > 0 && (
                        <span className="ml-2 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                          -{row.data.discount_percent}%
                        </span>
                      )}
                      {row.key === 'transport' && row.data.zone_name && (
                        <span className="ml-2 text-xs text-indigo-700">({row.data.zone_name})</span>
                      )}
                    </TableCell>
                    <TableCell className="font-manrope text-right">{formatCurrency(row.data.discounted_amount)}</TableCell>
                    <TableCell className="font-manrope text-right text-[#10B981]">{formatCurrency(row.data.paid_amount)}</TableCell>
                    <TableCell className={`font-manrope text-right font-semibold ${row.data.balance > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>
                      {formatCurrency(row.data.balance)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Today's Menu (always visible for parents) */}
      <Card className="border border-[#E2E8F0] shadow-none" data-testid="parent-today-menu-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <ForkKnife size={20} className="text-[#10B981]" weight="duotone" />
            {t('todayMenu') || (language === 'fr' ? 'Menu du jour' : "Today's menu")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {todayMenuLoading ? (
            <p className="text-sm text-[#94A3B8]">{language === 'fr' ? 'Chargement…' : 'Loading…'}</p>
          ) : todayMenu ? (
            <div className="text-sm space-y-1" data-testid="parent-today-menu">
              <p>🥗 <span className="font-medium">{t('starter') || (language === 'fr' ? 'Entrée' : 'Starter')}:</span> {todayMenu.starter || '—'}</p>
              <p>🍽️ <span className="font-medium">{t('mainCourse') || (language === 'fr' ? 'Plat' : 'Main')}:</span> {todayMenu.main || '—'}</p>
              <p>🍰 <span className="font-medium">{t('dessert') || (language === 'fr' ? 'Dessert' : 'Dessert')}:</span> {todayMenu.dessert || '—'}</p>
              {todayMenu.notes && <p className="text-xs italic text-[#64748B] mt-2">{todayMenu.notes}</p>}
            </div>
          ) : (
            <p className="text-sm text-[#94A3B8]" data-testid="parent-no-menu">
              {language === 'fr' ? 'Aucun menu publié aujourd\'hui' : 'No menu published today'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs defaultValue="bulletins" className="space-y-6">
        <TabsList className="bg-[#F1F5F9]">
          <TabsTrigger value="bulletins" className="data-[state=active]:bg-white" data-testid="tab-bulletins">
            <GraduationCap size={18} className="mr-2" />
            {t('childBulletins')}
          </TabsTrigger>
          <TabsTrigger value="payments" className="data-[state=active]:bg-white" data-testid="tab-payments">
            <Money size={18} className="mr-2" />
            {t('paymentHistory')}
          </TabsTrigger>
          {children.length > 1 && (
            <TabsTrigger value="compare" className="data-[state=active]:bg-white" data-testid="tab-compare">
              <Scales size={18} className="mr-2" />
              {language === 'fr' ? 'Comparaison' : 'Comparison'}
            </TabsTrigger>
          )}
          <TabsTrigger value="absences" className="data-[state=active]:bg-white" data-testid="tab-absences">
            <Warning size={18} className="mr-2" />
            {language === 'fr' ? 'Absences' : 'Absences'}
          </TabsTrigger>
          <TabsTrigger value="messages" className="data-[state=active]:bg-white" data-testid="tab-messages">
            <EnvelopeSimple size={18} className="mr-2" />
            {t('messages')}
            {messages.filter(m => m.status === 'unread' && m.recipient_id === user?.id).length > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-red-500 text-white text-xs">
                {messages.filter(m => m.status === 'unread' && m.recipient_id === user?.id).length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Bulletins Tab */}
        <TabsContent value="bulletins" className="space-y-4">
          {/* V2 — Bulletins automatiques (notes saisies par les enseignants) */}
          <Card className="border border-[#E2E8F0] shadow-none" data-testid="parent-bulletins-v2">
            <CardHeader className="pb-3">
              <CardTitle className="font-outfit text-base">
                {language === 'fr' ? 'Bulletins disponibles' : 'Available bulletins'}
              </CardTitle>
              <p className="text-xs text-[#64748B] mt-1">
                {language === 'fr'
                  ? 'Téléchargez le bulletin officiel généré à partir des notes saisies par les enseignants.'
                  : 'Download the official bulletin generated from the grades entered by teachers.'}
              </p>
            </CardHeader>
            <CardContent className="p-0">
              {availPeriods.length === 0 ? (
                <p className="text-sm text-[#94A3B8] text-center py-8">
                  {language === 'fr' ? 'Aucun bulletin disponible pour le moment.' : 'No bulletin available yet.'}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{language === 'fr' ? 'Période' : 'Period'}</TableHead>
                      <TableHead>{language === 'fr' ? 'Classe' : 'Class'}</TableHead>
                      <TableHead>{language === 'fr' ? 'Année' : 'Year'}</TableHead>
                      <TableHead>{language === 'fr' ? 'Matières notées' : 'Subjects'}</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {availPeriods.map((entry) => {
                      const key = `${entry.class_name}::${entry.period_key}`;
                      const label = entry.period_key.replace('trimestre-', 'Trimestre ')
                        .replace('semestre-', 'Semestre ')
                        .replace('periode-', 'Période ');
                      return (
                        <TableRow key={key} className="hover:bg-[#F1F5F9]" data-testid={`v2-bulletin-row-${entry.period_key}`}>
                          <TableCell>
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                              {label}
                            </span>
                          </TableCell>
                          <TableCell className="font-manrope">{entry.class_name}</TableCell>
                          <TableCell className="font-manrope text-[#64748B] text-xs">{entry.school_year}</TableCell>
                          <TableCell className="font-manrope text-[#64748B] text-xs">{entry.subjects_count}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm" variant="outline"
                              onClick={() => handleDownloadV2Bulletin(entry)}
                              disabled={downloadingBulletin === key}
                              className="border-[#0052FF] text-[#0052FF] hover:bg-blue-50"
                              data-testid={`download-v2-${entry.period_key}`}
                            >
                              <FilePdf size={14} className="mr-2" />
                              {downloadingBulletin === key ? '…' : (language === 'fr' ? 'Télécharger' : 'Download')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0]">
                    <TableHead className="font-outfit font-semibold">{t('date')}</TableHead>
                    <TableHead className="font-outfit font-semibold">{t('paymentType')}</TableHead>
                    <TableHead className="font-outfit font-semibold">{t('amount')}</TableHead>
                    <TableHead className="font-outfit font-semibold">{t('paymentMethod')}</TableHead>
                    <TableHead className="font-outfit font-semibold">{t('receipt')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentPaymentInfo?.payments?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-[#64748B]">{t('noData')}</TableCell>
                    </TableRow>
                  ) : (
                    currentPaymentInfo?.payments?.map((payment) => (
                      <TableRow key={payment.id} className="border-[#E2E8F0] hover:bg-[#F1F5F9]">
                        <TableCell className="font-manrope">{payment.created_at?.slice(0, 10)}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium
                            ${payment.payment_type === 'tuition' ? 'bg-blue-100 text-blue-700' :
                              payment.payment_type === 'canteen' ? 'bg-green-100 text-green-700' :
                              payment.payment_type === 'transport' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-purple-100 text-purple-700'}`}
                          >
                            {t(payment.payment_type)}
                          </span>
                        </TableCell>
                        <TableCell className="font-manrope font-semibold text-[#10B981]">
                          {formatCurrency(payment.amount)}
                        </TableCell>
                        <TableCell className="font-manrope">{t(payment.payment_method)}</TableCell>
                        <TableCell className="font-mono text-sm">{payment.receipt_number}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Comparaison Tab — Only visible when >1 child linked */}
        {children.length > 1 && (
          <TabsContent value="compare" className="space-y-4" data-testid="parent-compare-tab">
            {(() => {
              // Compute the priority child (highest outstanding balance)
              const priorityId = allPayments.reduce(
                (best, p) => (!best || (p.total?.balance || 0) > (best.balance || 0)
                  ? { id: p.student_id, balance: p.total?.balance || 0 }
                  : best),
                null,
              );
              const totals = allPayments.reduce(
                (acc, p) => ({
                  expected: acc.expected + (p.total?.expected || 0),
                  paid: acc.paid + (p.total?.paid || 0),
                  balance: acc.balance + (p.total?.balance || 0),
                }),
                { expected: 0, paid: 0, balance: 0 },
              );
              return (
                <>
                  {/* Header — overall summary across all children */}
                  <Card className="border border-[#E2E8F0] shadow-none bg-gradient-to-r from-[#F1F5F9] to-transparent" data-testid="compare-summary">
                    <CardContent className="p-5">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2.5 rounded-lg bg-blue-100">
                            <Scales size={22} className="text-blue-700" weight="duotone" />
                          </div>
                          <div>
                            <p className="font-outfit text-base font-semibold text-[#0F172A]">
                              {language === 'fr'
                                ? `Vue comparée des ${allPayments.length} enfants`
                                : `Compared view across ${allPayments.length} children`}
                            </p>
                            <p className="text-xs text-[#64748B] mt-1">
                              {language === 'fr'
                                ? 'Le dossier avec le solde le plus élevé est mis en avant. Cliquez sur un enfant pour ouvrir son détail.'
                                : 'The child with the highest outstanding balance is highlighted. Click a child to view their details.'}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-right shrink-0">
                          <div>
                            <p className="text-xs text-[#64748B]">{language === 'fr' ? 'Total dû' : 'Total due'}</p>
                            <p className="font-outfit text-lg font-bold text-[#0F172A]" data-testid="compare-total-expected">
                              {formatCurrency(totals.expected)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-[#64748B]">{language === 'fr' ? 'Payé' : 'Paid'}</p>
                            <p className="font-outfit text-lg font-bold text-[#10B981]" data-testid="compare-total-paid">
                              {formatCurrency(totals.paid)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-[#64748B]">{language === 'fr' ? 'Solde global' : 'Global balance'}</p>
                            <p className="font-outfit text-lg font-bold text-[#EF4444]" data-testid="compare-total-balance">
                              {formatCurrency(totals.balance)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Grid — one card per child */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="compare-grid">
                    {allPayments.map((p) => {
                      const child = children.find(c => c.id === p.student_id);
                      const isPriority = priorityId?.id === p.student_id && (p.total?.balance || 0) > 0;
                      const paidRatio = p.total?.expected > 0
                        ? Math.min(100, Math.round((p.total.paid / p.total.expected) * 100))
                        : 0;
                      const rows = [
                        { key: 'registration', label: p.registration?.label || (language === 'fr' ? 'Inscription' : 'Registration'), data: p.registration },
                        { key: 'tuition', label: language === 'fr' ? 'Scolarité' : 'Tuition', data: p.tuition },
                        { key: 'canteen', label: language === 'fr' ? 'Cantine' : 'Canteen', data: p.canteen },
                        { key: 'transport', label: language === 'fr' ? 'Transport' : 'Transport', data: p.transport },
                      ].filter(r => r.data && (r.data.discounted_amount > 0 || r.data.paid_amount > 0));
                      return (
                        <Card
                          key={p.student_id}
                          className={`border shadow-none transition ${isPriority ? 'border-amber-400 ring-1 ring-amber-300 bg-amber-50/30' : 'border-[#E2E8F0] hover:border-[#10B981]/50'}`}
                          data-testid={`compare-card-${p.student_id}`}
                        >
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <CardTitle className="font-outfit text-base leading-tight">
                                  {p.student_name}
                                </CardTitle>
                                <p className="text-xs text-[#64748B] font-manrope mt-0.5">
                                  {language === 'fr' ? 'Classe' : 'Class'} : {p.class_id || '—'}
                                </p>
                              </div>
                              {isPriority && (
                                <span
                                  className="inline-flex items-center gap-1 shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-500 text-white"
                                  data-testid={`compare-priority-badge-${p.student_id}`}
                                >
                                  <Warning size={11} weight="fill" />
                                  {language === 'fr' ? 'Prioritaire' : 'Priority'}
                                </span>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {/* Progress bar */}
                            <div>
                              <div className="flex items-center justify-between mb-1 text-xs text-[#64748B]">
                                <span>{language === 'fr' ? 'Progression du paiement' : 'Payment progress'}</span>
                                <span className="font-semibold text-[#0F172A]" data-testid={`compare-ratio-${p.student_id}`}>{paidRatio}%</span>
                              </div>
                              <div className="h-2 rounded-full bg-[#F1F5F9] overflow-hidden">
                                <div
                                  className={`h-full ${paidRatio >= 100 ? 'bg-[#10B981]' : paidRatio >= 50 ? 'bg-[#0052FF]' : 'bg-amber-500'}`}
                                  style={{ width: `${paidRatio}%` }}
                                />
                              </div>
                            </div>

                            {/* Totals mini-grid */}
                            <div className="grid grid-cols-3 gap-2 pt-1">
                              <div className="rounded-md bg-[#F8FAFC] px-2 py-2 text-center">
                                <p className="text-[10px] uppercase text-[#64748B]">{language === 'fr' ? 'Dû' : 'Due'}</p>
                                <p className="font-outfit text-sm font-bold text-[#0F172A]" data-testid={`compare-due-${p.student_id}`}>
                                  {formatCurrency(p.total?.expected)}
                                </p>
                              </div>
                              <div className="rounded-md bg-emerald-50 px-2 py-2 text-center">
                                <p className="text-[10px] uppercase text-[#64748B]">{language === 'fr' ? 'Payé' : 'Paid'}</p>
                                <p className="font-outfit text-sm font-bold text-[#10B981]" data-testid={`compare-paid-${p.student_id}`}>
                                  {formatCurrency(p.total?.paid)}
                                </p>
                              </div>
                              <div className={`rounded-md px-2 py-2 text-center ${(p.total?.balance || 0) > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                                <p className="text-[10px] uppercase text-[#64748B]">{language === 'fr' ? 'Solde' : 'Balance'}</p>
                                <p
                                  className={`font-outfit text-sm font-bold ${(p.total?.balance || 0) > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}
                                  data-testid={`compare-balance-${p.student_id}`}
                                >
                                  {formatCurrency(p.total?.balance)}
                                </p>
                              </div>
                            </div>

                            {/* Breakdown by category */}
                            {rows.length > 0 && (
                              <div className="border-t border-[#E2E8F0] pt-3 space-y-1.5">
                                {rows.map(row => (
                                  <div key={row.key} className="flex items-center justify-between text-xs font-manrope">
                                    <span className="text-[#475569]">
                                      {row.label}
                                      {row.data.discount_percent > 0 && (
                                        <span className="ml-1.5 text-[10px] text-amber-700 bg-amber-50 px-1 py-0.5 rounded">
                                          -{row.data.discount_percent}%
                                        </span>
                                      )}
                                    </span>
                                    <span className={`font-semibold ${(row.data.balance || 0) > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>
                                      {formatCurrency(row.data.balance)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Actions */}
                            <div className="flex flex-col gap-2 pt-1">
                              {isPriority && (
                                <Button
                                  onClick={() => payPriorityGeniusPay(p)}
                                  disabled={payingCategory === `priority:${p.student_id}` || payingCategory !== null}
                                  size="sm"
                                  className="w-full bg-amber-500 hover:bg-amber-600 text-white shadow-sm"
                                  data-testid={`compare-pay-priority-${p.student_id}`}
                                >
                                  <CreditCard size={14} className="mr-2" />
                                  {payingCategory === `priority:${p.student_id}`
                                    ? (language === 'fr' ? 'Redirection…' : 'Redirecting…')
                                    : (language === 'fr' ? 'Payer le dossier prioritaire' : 'Pay priority balance')}
                                </Button>
                              )}
                              <Button
                                onClick={() => {
                                  if (child) setSelectedChild(child);
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                variant="outline"
                                size="sm"
                                className="w-full border-[#0052FF] text-[#0052FF] hover:bg-blue-50"
                                data-testid={`compare-open-${p.student_id}`}
                              >
                                <Eye size={14} className="mr-2" />
                                {language === 'fr' ? 'Voir le dossier' : 'Open details'}
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  {allPayments.length === 0 && (
                    <Card className="border border-[#E2E8F0] shadow-none">
                      <CardContent className="p-8 text-center text-[#94A3B8] font-manrope text-sm">
                        {language === 'fr' ? 'Aucune donnée de paiement disponible.' : 'No payment data available.'}
                      </CardContent>
                    </Card>
                  )}
                </>
              );
            })()}
          </TabsContent>
        )}

        {/* Absences Tab — parent can justify their own child's absences */}
        <TabsContent value="absences" className="space-y-4" data-testid="parent-absences-tab">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="font-outfit text-base flex items-center gap-2">
                  <Warning size={20} className="text-red-600" weight="duotone" />
                  {language === 'fr' ? 'Absences des 60 derniers jours' : 'Absences (last 60 days)'}
                  {selectedChild && (
                    <span className="text-xs font-normal text-[#64748B] ml-2">
                      {selectedChild.first_name} {selectedChild.last_name}
                    </span>
                  )}
                </CardTitle>
                <span
                  className="text-xs bg-red-50 text-red-700 px-3 py-1 rounded-full font-semibold"
                  data-testid="parent-absences-count"
                >
                  {absences.length} {language === 'fr' ? 'absence(s)' : 'absence(s)'}
                </span>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {absencesLoading ? (
                <div className="p-8 text-center text-[#94A3B8]">
                  {language === 'fr' ? 'Chargement…' : 'Loading…'}
                </div>
              ) : absences.length === 0 ? (
                <div className="p-10 text-center" data-testid="parent-absences-empty">
                  <ShieldCheck size={40} className="mx-auto text-emerald-500 mb-2" weight="duotone" />
                  <p className="font-outfit font-semibold text-[#0F172A]">
                    {language === 'fr' ? 'Aucune absence enregistrée' : 'No absence recorded'}
                  </p>
                  <p className="text-sm text-[#64748B]">
                    {language === 'fr' ? 'Rien à signaler sur la période.' : 'Nothing to report over the period.'}
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>{language === 'fr' ? 'Matière' : 'Subject'}</TableHead>
                      <TableHead>{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {absences.map((a) => (
                      <TableRow key={a._mongo_id || a.id} data-testid={`parent-absence-row-${a._mongo_id || a.id}`}>
                        <TableCell className="font-mono text-xs">{a.date}</TableCell>
                        <TableCell className="text-xs">
                          {a.subject || (a.slot_key === 'daily'
                            ? (language === 'fr' ? 'Journée entière' : 'Full day')
                            : '—')}
                        </TableCell>
                        <TableCell>
                          {a.justification ? (
                            <div className="flex items-center gap-1.5 text-xs" data-testid={`parent-absence-justified-${a._mongo_id || a.id}`}>
                              <ShieldCheck size={14} className="text-emerald-600" weight="fill" />
                              <span className="text-emerald-700 font-medium">
                                {language === 'fr' ? 'Justifiée' : 'Justified'}
                              </span>
                              {a.justification.document_filename && (
                                <PaperclipHorizontal size={12} className="text-[#64748B]" />
                              )}
                            </div>
                          ) : (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">
                              {language === 'fr' ? 'À justifier' : 'To justify'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {a.justification ? (
                            <div className="flex items-center justify-end gap-2">
                              <span className="text-xs text-[#94A3B8] italic">
                                {a.justification.reason?.slice(0, 40)}
                                {(a.justification.reason || '').length > 40 && '…'}
                              </span>
                              <button
                                onClick={async () => {
                                  try {
                                    const { studentAttendanceApi: satt } = await import('../lib/api');
                                    const res = await satt.justificationPdf(a._mongo_id || a.id);
                                    const blob = new Blob([res.data], { type: 'application/pdf' });
                                    const url = URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = `justificatif-${a.date}.pdf`;
                                    document.body.appendChild(link); link.click(); link.remove();
                                    window.open(url, '_blank');
                                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                                  } catch (e) {
                                    toast.error(language === 'fr' ? 'Erreur téléchargement' : 'Download failed');
                                  }
                                }}
                                className="text-[11px] px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-medium"
                                data-testid={`parent-download-justif-${a._mongo_id || a.id}`}
                              >
                                PDF
                              </button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => openJustify(a)}
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`parent-justify-btn-${a._mongo_id || a.id}`}
                            >
                              <ShieldCheck size={14} className="mr-1" />
                              {language === 'fr' ? 'Justifier' : 'Justify'}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Justify Absence Dialog */}
        <Dialog open={justifyOpen} onOpenChange={(o) => { setJustifyOpen(o); if (!o) setJustifyTarget(null); }}>
          <DialogContent className="max-w-lg" data-testid="parent-justify-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit">
                {language === 'fr' ? 'Justifier l\'absence' : 'Justify absence'}
                {justifyTarget && (
                  <span className="block text-sm font-normal text-[#64748B] mt-1">
                    {justifyTarget.student_name} · {justifyTarget.class_name} · {justifyTarget.date}
                    {justifyTarget.subject && ` · ${justifyTarget.subject}`}
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{language === 'fr' ? 'Motif *' : 'Reason *'}</Label>
                <Textarea
                  value={justifyReason}
                  onChange={(e) => setJustifyReason(e.target.value)}
                  placeholder={language === 'fr' ? 'Ex : Maladie, rendez-vous médical…' : 'e.g. Illness, medical appointment…'}
                  rows={4}
                  maxLength={1000}
                  data-testid="parent-justify-reason-input"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>
                  {language === 'fr'
                    ? 'Justificatif scanné (image ou PDF, ≤3 Mo — optionnel)'
                    : 'Scanned proof (image or PDF, ≤3 MB — optional)'}
                </Label>
                <Input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={onPickJustifyFile}
                  className="mt-1"
                  data-testid="parent-justify-file-input"
                />
                {justifyFileName && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                    <PaperclipHorizontal size={14} />
                    <span className="truncate">{justifyFileName}</span>
                    <button
                      onClick={() => { setJustifyFileB64(''); setJustifyFileName(''); }}
                      className="ml-auto text-red-600 hover:text-red-700"
                    >
                      <XCircle size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setJustifyOpen(false)}
                disabled={justifySubmitting}
                data-testid="parent-justify-cancel-btn"
              >
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button
                onClick={submitJustify}
                disabled={justifySubmitting || !justifyReason.trim()}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="parent-justify-submit-btn"
              >
                <ShieldCheck size={16} className="mr-2" />
                {justifySubmitting
                  ? (language === 'fr' ? 'Envoi…' : 'Sending…')
                  : (language === 'fr' ? 'Justifier' : 'Justify')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Messages Tab */}
        <TabsContent value="messages" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => setMessageDialogOpen(true)}
              className="bg-[#10B981] hover:bg-[#059669]"
              data-testid="new-message-btn"
            >
              <PaperPlaneTilt size={18} className="mr-2" />
              {t('newMessage')}
            </Button>
          </div>
          
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0]">
                    <TableHead className="font-outfit font-semibold">{t('date')}</TableHead>
                    <TableHead className="font-outfit font-semibold">{t('subject')}</TableHead>
                    <TableHead className="font-outfit font-semibold">{t('from')}/{t('to')}</TableHead>
                    <TableHead className="font-outfit font-semibold">Status</TableHead>
                    <TableHead className="font-outfit font-semibold text-right">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {messages.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-[#64748B]">{t('noData')}</TableCell>
                    </TableRow>
                  ) : (
                    messages.map((message) => (
                      <TableRow 
                        key={message.id} 
                        className={`border-[#E2E8F0] hover:bg-[#F1F5F9] ${message.status === 'unread' && message.recipient_id === user?.id ? 'bg-[#10B981]/5' : ''}`}
                      >
                        <TableCell className="font-manrope">{message.created_at?.slice(0, 10)}</TableCell>
                        <TableCell className="font-manrope font-medium">{message.subject}</TableCell>
                        <TableCell className="font-manrope">
                          {message.sender_id === user?.id 
                            ? `→ ${language === 'fr' ? 'École' : 'School'}`
                            : `← ${message.sender_name}`}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium
                            ${message.status === 'unread' ? 'bg-yellow-100 text-yellow-700' :
                              message.status === 'read' ? 'bg-blue-100 text-blue-700' :
                              'bg-green-100 text-green-700'}`}
                          >
                            {t(message.status)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewMessage(message)}
                            className="hover:bg-[#10B981]/10 text-[#10B981]"
                          >
                            <Eye size={18} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Message Dialog */}
      <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">{t('newMessage')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendMessage} className="space-y-4">
            <div className="space-y-2">
              <Label className="font-manrope">{t('subject')}</Label>
              <Input
                value={newMessage.subject}
                onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                placeholder={language === 'fr' ? 'Objet du message' : 'Message subject'}
                required
                className="border-[#E2E8F0]"
                data-testid="message-subject"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('message')}</Label>
              <Textarea
                value={newMessage.content}
                onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                placeholder={language === 'fr' ? 'Votre message...' : 'Your message...'}
                required
                rows={5}
                className="border-[#E2E8F0]"
                data-testid="message-content"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMessageDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-[#10B981] hover:bg-[#059669]" data-testid="send-message-btn">
                <PaperPlaneTilt size={18} className="mr-2" />
                {t('sendMessage')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Message Detail Dialog */}
      <Dialog open={messageDetailOpen} onOpenChange={setMessageDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">{selectedMessage?.subject}</DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4">
              <div className="text-sm text-[#64748B] font-manrope">
                {t('from')}: {selectedMessage.sender_name} • {selectedMessage.created_at?.slice(0, 16).replace('T', ' ')}
              </div>
              
              <div className="p-4 bg-[#F8FAFC] rounded-lg">
                <p className="font-manrope whitespace-pre-wrap">{selectedMessage.content}</p>
              </div>
              
              {/* Replies */}
              {selectedMessage.replies?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-outfit font-semibold">{language === 'fr' ? 'Réponses' : 'Replies'}</h4>
                  {selectedMessage.replies.map((reply, idx) => (
                    <div key={idx} className={`p-4 rounded-lg ${reply.sender_role === 'parent' ? 'bg-[#10B981]/10 ml-4' : 'bg-[#F1F5F9] mr-4'}`}>
                      <div className="text-xs text-[#64748B] mb-1">
                        {reply.sender_name} • {reply.created_at?.slice(0, 16).replace('T', ' ')}
                      </div>
                      <p className="font-manrope text-sm">{reply.content}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Reply Form */}
              <div className="space-y-2 border-t pt-4">
                <Label className="font-manrope">{t('reply')}</Label>
                <Textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder={language === 'fr' ? 'Votre réponse...' : 'Your reply...'}
                  rows={3}
                  className="border-[#E2E8F0]"
                />
                <div className="flex justify-end">
                  <Button onClick={handleReply} className="bg-[#10B981] hover:bg-[#059669]">
                    <PaperPlaneTilt size={18} className="mr-2" />
                    {t('reply')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Add-child ("+ Ajouter un enfant") Dialog */}
      <Dialog open={linkChildOpen} onOpenChange={(open) => {
        setLinkChildOpen(open);
        if (!open) setLinkChildCode('');
      }}>
        <DialogContent className="max-w-md" data-testid="add-child-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">
              {language === 'fr' ? 'Ajouter un enfant' : 'Add a child'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLinkChild} className="space-y-4">
            <div className="space-y-2">
              <Label className="font-manrope">
                {language === 'fr' ? "Code élève" : 'Student code'}
              </Label>
              <Input
                value={linkChildCode}
                onChange={(e) => setLinkChildCode(e.target.value)}
                placeholder={language === 'fr' ? "Ex : ELV-2025-A1B2" : 'e.g. STU-2025-A1B2'}
                required
                autoFocus
                className="border-[#E2E8F0]"
                data-testid="add-child-code-input"
              />
              <p className="text-xs text-[#64748B]">
                {language === 'fr'
                  ? "Saisissez le code communiqué par l'école pour lier cet enfant à votre compte."
                  : 'Enter the code provided by the school to link this child to your account.'}
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setLinkChildOpen(false)}
                disabled={linkChildSubmitting}
                data-testid="add-child-cancel-btn"
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                className="bg-[#10B981] hover:bg-[#059669]"
                disabled={linkChildSubmitting || !linkChildCode.trim()}
                data-testid="add-child-submit-btn"
              >
                <UserPlus size={18} className="mr-2" />
                {linkChildSubmitting
                  ? (language === 'fr' ? 'Ajout…' : 'Adding…')
                  : (language === 'fr' ? 'Ajouter' : 'Add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentDashboard;
